"use client";

import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { VybeTraderClient } from "../client/VybeTraderClient";
import { useSubscribedSymbolsSync } from "../../subscribed-symbols/hooks/useSubscribedSymbolsSync";
import { useWakeWatchdog } from "../hooks/useWakeWatchdog";
import type { ConnectionStatus } from "../../shared/types";

interface VybeTraderContextType {
  client: VybeTraderClient;
  connectionStatus: ConnectionStatus;
}

export const VybeTraderContext = createContext<VybeTraderContextType | null>(
  null
);

export default function VybeTraderClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [client] = useState(() => VybeTraderClient.getInstance());
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const previousConnectionStatusRef = useRef<ConnectionStatus>("disconnected");
  const hasInitialized = useRef(false);

  useSubscribedSymbolsSync();
  // Sleep/wake freshness gate (proposal 23): detect resume, freeze + mark stale,
  // recycle the suspect socket, and snapshot-resync.
  useWakeWatchdog(client);

  const clearAllStores = useCallback(() => {
    Promise.all([
      import("../../messaging/stores/useRawMessagesStore"),
      import("../../symbols/stores/useSymbolsStore"),
      import("../../account-information/stores/useAccountInformationStore"),
      import("../../opened-trades/stores/useOpenedTradesStore"),
      import("../../trade-history/stores/useTradeHistoryStore"),
      import("../../order-book/stores/useMarketDepthStore"),
    ])
      .then(
        ([
          { useRawMessagesStore },
          { useSymbolsStore },
          { useAccountInformationStore },
          { useOpenedTradesStore },
          { useTradeHistoryStore },
          { useMarketDepthStore },
        ]) => {
          useRawMessagesStore.getState().clear();
          useSymbolsStore.getState().reset();
          useAccountInformationStore.getState().reset();
          useOpenedTradesStore.getState().reset();
          useTradeHistoryStore.getState().reset();
          useMarketDepthStore.getState().reset();
        }
      )
      .catch((error) => {
        console.error("[Provider] Failed to clear stores:", error);
      });
  }, []);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const interval = setInterval(() => {
      setConnectionStatus(client.getConnectionStatus());
    }, 1000);

    return () => clearInterval(interval);
  }, [client]);

  // Close the trading socket when the page is actually going away (a refresh, a navigation — NOT a
  // bfcache park or an iOS background, which arrive as `persisted` / visibilitychange). Without it
  // the gateway kept the old session for up to its 40s server timeout, and the new page — dialling
  // within a second of a PWA refresh — logged in against a session that was still held.
  useEffect(() => {
    const onPageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return;
      void client.disconnect().catch(() => {
        /* page is unloading — best effort */
      });
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [client]);

  useEffect(() => {
    const previousStatus = previousConnectionStatusRef.current;

    if (previousStatus === "connected" && connectionStatus === "disconnected") {
      clearAllStores();
    }

    previousConnectionStatusRef.current = connectionStatus;
  }, [connectionStatus, clearAllStores]);

  return (
    <VybeTraderContext.Provider value={{ client, connectionStatus }}>
      {children}
    </VybeTraderContext.Provider>
  );
}
